import { useLocation } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState } from '../store'
import { taskKindLabel, type TaskSummary, type TestSession, type Project } from '../data/projectTypes'
import {
  useAppendProjectChatMessagesMutation,
  useGetBugCountsQuery,
  useGetProjectChatMessagesQuery,
  useGetProjectJourneysInfiniteQuery,
  useGetProjectTestSessionsInfiniteQuery,
  useGetProjectTaskActivityQuery,
  useGetProcessingSummaryQuery,
  PROJECT_LIST_PAGE_SIZE,
  useUpdateProjectMutation,
  projectDataApi,
} from '../services/projectDataApi'
import { useNavigate } from './nav'
import ChatMessageList, { type ChatListItem } from './chat/ChatMessageList'
import TestRunEntry from './chat/TestRunCards'
import ChatInput from './chat/ChatInput'
import ChatLoginsForm from './chat/ChatLoginsForm'
import ChatAccessRequests, { VercelAccessSettings } from './chat/ChatAccessRequests'
import ChatChoices from './chat/ChatChoices'
import ProjectGithubChatCard from './chat/ProjectGithubChatCard'
import IssueTrackerChatCard from './chat/IssueTrackerChatCard'
import { SecurityOwnershipCard, type SecurityNonceState } from './chat/SecurityNonceChatCard'
import { describeGithubSettings } from '../lib/githubTesting'
import { projectPause } from '../lib/projectPause'
import { createMessage, replaceMessage, toWireMessages, type ChatMessageData } from './chat/chatModel'
import { capTranscript } from '../../netlify/functions/lib/project-chat-transcript'
import { runChatTurn } from './chat/pollChat'
import { reportChatFailure, reportChatStall, chatFailureNotice, turnExists, type ChatTurnContext } from './chat/chatTelemetry'
import { onChatPromptRequest, onChatPromptFillRequest, takePendingChatPrompt } from '../lib/chatPrompt'
import { type LoginEntry, type LoginMode } from './qaConfig'
import { mergeProvidedLoginGlobalVariables } from './projectLoginVariables'
import { currentChatTimezone } from '../lib/chatTimezone'
import { loginChoiceSet, type ChatChoice, type ChatChoiceSet, type ChatFormRequest, type GithubPanelFocus, type IssueTrackerProvider } from '../../netlify/functions/lib/project-chat-types'
import './chat/chat.css'
import './ProjectChat.css'

// The collapsible chat sidebar on the (experimental) project overview page. It shows what QA has been
// doing (a live activity timeline built from the task history/queue) alongside the conversation the
// user has had with QA, and lets them ask questions or change settings. Chat history is persisted per
// project in localStorage (there is no server-side chat store); the QA activity comes from the real
// processing slice. Chat client model/components are adapted from replayio/nut.new.

// How many test runs the per-turn context lists. One line is ~190 chars, so a long-lived project spent
// most of its prompt on this list alone (515 runs = ~97KB, about 80% of a 121KB system prompt) — re-sent
// and re-processed on every iteration of every turn until the turn ran out of its time budget mid-reply.
// The runs beyond this are not lost: read_test_runs lists all of them, and takes a `query` to filter.
const MAX_CONTEXT_TEST_RUNS = 30

function relTime(iso: string | null): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.round(hrs / 24)}d ago`
}

function taskLabel(task: TaskSummary): string {
  return task.goal?.trim() || task.journey_name?.trim() || `${taskKindLabel(task)} task`
}

function taskTimeMs(iso: string | null | undefined): number {
  if (!iso) return NaN
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? NaN : t
}

// An absolute timestamp with a relative gloss, e.g. "2026-07-10T12:00:00Z (3h ago)", so the agent can
// reason both about exactly when something happened and how recent it is relative to the current time.
function absTime(iso: string | null | undefined): string {
  if (!iso) return ''
  if (Number.isNaN(new Date(iso).getTime())) return ''
  const rel = relTime(iso)
  return rel ? `${iso} (${rel})` : iso
}

// Rendered inside the app's main sidebar (AppShell) when the new-project-overview experiment is on
// and the user is viewing a project overview — see AppShell's chat-mode sidebar. It fills the lower
// portion of the sidebar; the sidebar owns collapsing, so this component no longer collapses itself.
export default function ProjectChat({
  projectId,
  project,
  onOpenProjectItem,
  onAssistantMessage,
}: {
  projectId: string
  project: Project | null
  // Called when the user opens an item (exploration / journey / bug) from a test-run entry. On the
  // mobile chat layout AppShell uses this to switch the visible pane from the chat to the project
  // view (on desktop the two panes are side by side, so it's a no-op).
  onOpenProjectItem?: () => void
  // Called when a *new* agent (assistant) message lands in the transcript after the initial history
  // has loaded — a streamed reply, an inline confirmation, or a server-posted message picked up by the
  // poll. On the mobile chat layout AppShell uses this to flag the Chat tab when the project pane is
  // showing, so the user knows to switch back. A no-op on desktop (both panes are always visible).
  onAssistantMessage?: () => void
}) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [updateProject] = useUpdateProjectMutation()
  const dispatch = useDispatch()
  // The agent's settings / pause-resume tools write to the database themselves now, so the browser's
  // job is only to notice. Invalidating the tag re-reads whatever actually landed, rather than
  // re-issuing a write the server has already performed.
  const refetchProject = useCallback(() => {
    dispatch(projectDataApi.util.invalidateTags([
      { type: 'Project' as const, id: projectId },
      { type: 'ProjectEnvironments' as const, id: projectId },
      { type: 'ProjectUserSessions' as const, id: projectId },
      { type: 'ProjectJourneys' as const, id: projectId },
    ]))
  }, [dispatch, projectId])
  const [appendProjectChatMessages] = useAppendProjectChatMessagesMutation()
  // The signed-in member. Their turns are attributed to them (so other members see who said what, and
  // they chat as themselves) and their own turns render without an author label.
  const currentUser = useSelector((s: RootState) => s.auth.user)
  const [livePollingInterval, setLivePollingInterval] = useState(60_000)

  const [messages, setMessages] = useState<ChatMessageData[]>([])
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // What the agent is doing right now ("Reading your test runs…"), streamed as `status` events before
  // any text exists. The handler has always been in the protocol but nothing passed it, so a turn that
  // spent 20s in tool calls showed a bare spinner — and if it then died, showed nothing at all.
  const [agentStatus, setAgentStatus] = useState<string | null>(null)
  const [pendingForm, setPendingForm] = useState<ChatFormRequest | null>(null)
  // Ownership is re-checked by the runner before every run, so a result here is only ever a live
  // answer for the card — deliberately not persisted, where it could go stale in the unsafe direction.
  const [securityChecked, setSecurityChecked] = useState<SecurityNonceState | null>(null)
  // Clickable choices the agent offered this turn (via offer_choices), rendered as buttons.
  const [pendingChoices, setPendingChoices] = useState<ChatChoiceSet | null>(null)
  // The inline GitHub connect/disconnect panel: null when hidden, otherwise the part of the flow the
  // agent (manage_github) asked to focus on — 'all' shows the full panel, 'main-branch' / 'pull-requests'
  // open straight to that one testing question on an already-connected repo.
  const [githubPanel, setGithubPanel] = useState<{ focus: GithubPanelFocus; repoUrl?: string; resumePrompt?: string } | null>(null)
  // The inline issue-tracker config panel (manage_issue_tracker): null when hidden, otherwise the
  // provider the agent asked to preselect ('' means "no preselection — use the configured/default one").
  const [issueTracker, setIssueTracker] = useState<{ provider?: IssueTrackerProvider } | null>(null)
  // Set once the user has acted on the "exploration paused for login" choices, so the built-in choice
  // set is hidden immediately (login_mode also flips off 'waiting', but that round-trips through the DB).
  const [loginPauseResolved, setLoginPauseResolved] = useState(false)
  // Mirror of `messages` so runTurn can build the next conversation without a side effect inside a
  // setState updater (StrictMode double-invokes updaters in dev).
  const messagesRef = useRef<ChatMessageData[]>(messages)
  messagesRef.current = messages
  // IDs already stored server-side, so we never re-POST a turn loaded from RTK Query.
  const persistedIdsRef = useRef<Set<string>>(new Set())
  const chatHistoryInitializedRef = useRef(false)
  // Turns that came out of the initial history load, so they are rendered WITHOUT a scroll anchor (see
  // ChatListItem.anchor): the scroller re-anchors to the first anchor it has never scrolled to whenever
  // the log's row count is unchanged but its rows moved, which is how an unrelated update — a test run
  // finishing, say — used to throw the reader back to the conversation's very first message. Only turns
  // that arrive while the chat is open anchor, which is where the behaviour is actually wanted.
  const historyMessageIdsRef = useRef<Set<string>>(new Set())
  // Assistant message ids we've already reported via `onAssistantMessage`, so a streamed reply (whose
  // id is stable across deltas) fires the cue exactly once and messages re-seen on a poll never fire
  // again. The initial history load seeds this set silently — only messages arriving afterwards count
  // as "new". Kept in a ref (not state) so seeding it never triggers a re-render.
  const notifiedAssistantIdsRef = useRef<Set<string>>(new Set())
  const onAssistantMessageRef = useRef(onAssistantMessage)
  onAssistantMessageRef.current = onAssistantMessage

  // Record assistant messages as seen; when `notify` is set, fire `onAssistantMessage` once per newly
  // seen assistant message. User messages and already-seen ids are ignored. Called outside React's
  // setState updaters so the parent's state update never runs during this component's render.
  const markAssistantSeen = useCallback((msgs: { id: string; role: string }[], notify: boolean) => {
    for (const m of msgs) {
      if (m.role !== 'assistant' || notifiedAssistantIdsRef.current.has(m.id)) continue
      notifiedAssistantIdsRef.current.add(m.id)
      if (notify) onAssistantMessageRef.current?.()
    }
  }, [])

  // RTK Query owns the server data used by the activity context and transcript.
  const activityQuery = useGetProjectTaskActivityQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const processingQuery = useGetProcessingSummaryQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const activity = activityQuery.data ?? null
  const running = processingQuery.data?.running ?? []
  const queued = processingQuery.data?.queued ?? []
  const testSessionsQuery = useGetProjectTestSessionsInfiniteQuery(
    { projectId, pageSize: PROJECT_LIST_PAGE_SIZE },
    { pollingInterval: livePollingInterval, skipPollingIfUnfocused: true, refetchCachedPages: false },
  )
  const testSessionPage = testSessionsQuery.data?.pages[0]
  const testSessions = testSessionPage?.items.slice(0, MAX_CONTEXT_TEST_RUNS) ?? []
  const testSessionsTotal = testSessionPage?.total ?? testSessions.length
  const journeyQuery = useGetProjectJourneysInfiniteQuery(
    { projectId, parentId: null, pageSize: PROJECT_LIST_PAGE_SIZE },
    { pollingInterval: livePollingInterval, skipPollingIfUnfocused: true, refetchCachedPages: false },
  )
  const journeyPage = journeyQuery.data?.pages[0]
  // Summary counts for the agent's context.
  const journeyTotal = journeyPage?.totalAll ?? journeyPage?.total ?? 0
  const journeyStatusCounts = journeyPage?.statusCounts ?? {}
  const chatQuery = useGetProjectChatMessagesQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const bugCountsQuery = useGetBugCountsQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const bugTotal = bugCountsQuery.data?.total ?? 0
  const bugOpen = bugCountsQuery.data?.openCount ?? 0

  const projectRunning = useMemo(() => running.filter((t) => t.project_id === projectId), [running, projectId])
  const projectQueued = useMemo(() => queued.filter((t) => t.project_id === projectId), [queued, projectId])
  const hasLiveJourneyState = projectRunning.length > 0
    || projectQueued.length > 0
    || testSessions.some(session => session.status === 'in-progress')
  useEffect(() => {
    setLivePollingInterval(hasLiveJourneyState ? 5_000 : 60_000)
  }, [hasLiveJourneyState])

  // Merge a batch fetched from the DB into the transcript, deduped by id (so our own just-posted turns,
  // and re-fetches, are ignored). Advances the poll cursor and marks the ids as already persisted.
  // `notify` marks whether newly merged assistant messages should raise the new-message cue: false for
  // the initial history load (those are pre-existing), true for the background poll (a server posted a
  // fresh message). Fresh messages are computed against messagesRef (kept in sync every render) so the
  // assistant-seen bookkeeping happens outside the setState updater.
  const mergeIncoming = useCallback((incoming: ChatMessageData[], notify = false) => {
    if (incoming.length === 0) return
    incoming.forEach((m) => persistedIdsRef.current.add(m.id))
    const have = new Set(messagesRef.current.map((m) => m.id))
    const fresh = incoming.filter((m) => !have.has(m.id))
    if (fresh.length === 0) return
    const next = [...messagesRef.current, ...fresh]
    messagesRef.current = next
    setMessages(next)
    markAssistantSeen(fresh, notify)
  }, [markAssistantSeen])

  // Reset the local presentation when changing projects. The persisted transcript itself is owned by
  // the RTK Query entry above, including its polling and request deduplication.
  useEffect(() => {
    persistedIdsRef.current = new Set()
    chatHistoryInitializedRef.current = false
    notifiedAssistantIdsRef.current = new Set()
    historyMessageIdsRef.current = new Set()
    setMessages([])
    messagesRef.current = []
    setPendingForm(null)
    setPendingChoices(null)
    setGithubPanel(null)
    setIssueTracker(null)
    setLoginPauseResolved(false)
  }, [projectId])

  useEffect(() => {
    if (!chatQuery.data) return
    const notify = chatHistoryInitializedRef.current
    // Everything in the first batch is history (the set is read when the items are built, below).
    if (!notify) chatQuery.data.forEach((m) => historyMessageIdsRef.current.add(m.id))
    mergeIncoming(chatQuery.data, notify)
    chatHistoryInitializedRef.current = true
  }, [chatQuery.data, mergeIncoming])

  // Persist locally-added turns (user sends, streamed replies, inline confirmations) to the DB. Idempotent
  // by id, and skips anything already persisted (loaded/polled/previously sent).
  const persistMessages = useCallback((msgs: ChatMessageData[]) => {
    const fresh = msgs.filter((m) => m.id && !persistedIdsRef.current.has(m.id))
    if (fresh.length === 0) return
    fresh.forEach((m) => persistedIdsRef.current.add(m.id))
    void appendProjectChatMessages({ projectId, messages: fresh })
  }, [appendProjectChatMessages, projectId])

  // Build the grounding context the agent sees each turn: the current time, project settings, and the
  // full activity picture — every test run with its explorations and journeys, each with its status
  // and timestamps — so the agent can answer precisely about what QA is doing and when it happened.
  const buildContext = useCallback((): string => {
    const openBugs = bugOpen
    const jInProgress = (journeyStatusCounts['in-progress'] ?? 0) + (journeyStatusCounts['queued'] ?? 0)
    const lines: string[] = []
    // The current time anchors every relative/absolute timestamp below (e.g. "how long has this been
    // running?", "is that recent?"). Kept at the top so the agent always has it.
    lines.push(`Current time: ${new Date().toISOString()}`)
    lines.push(`Current project page: ${pathname}${search}`)
    if (project) {
      lines.push(`Project: ${project.name} (status: ${project.status})`)
      if (project.target_url) lines.push(`App URL: ${project.target_url}`)
      if (project.github?.repoUrl) lines.push(...describeGithubSettings(project.github))
      const it = project.issue_tracker
      lines.push(it && it.enabled
        ? `Issue tracker: filing new bugs to ${it.provider === 'github' ? `GitHub (${it.githubRepoUrl ?? 'repo'})` : it.provider === 'gitlab' ? `GitLab (${it.gitlabProjectUrl ?? 'project'})` : it.provider === 'linear' ? 'Linear' : `Jira (${it.jiraBaseUrl ?? ''} / ${it.jiraProjectKey ?? ''})`}.`
        : 'Issue tracker: none configured (bugs are tracked in Replay QA only).')
      const sd = project.summary_destinations
      const summaryChannels = sd
        ? [sd.slack?.webhookUrlSet ? 'Slack' : '', sd.discord?.webhookUrlSet ? 'Discord' : '', sd.email ? `Email (${sd.email.recipients})` : ''].filter(Boolean)
        : []
      lines.push(summaryChannels.length
        ? `Daily summary destinations: publishing to ${summaryChannels.join(', ')}.`
        : 'Daily summary destinations: none configured (summaries are only viewable in Replay QA).')
      lines.push(`Budget: ${project.budget == null ? 'unlimited' : `${project.budget} credits`}`)
      lines.push(`Polish passes enabled: ${project.enabled_polish_passes.join(', ') || 'none'}`)
      lines.push(`Design document: ${project.design_document ? 'provided' : 'none'}`)
    }
    lines.push(`Journeys: ${journeyTotal} total, ${jInProgress} in progress.`)
    lines.push(`Bugs: ${bugTotal} total, ${openBugs} open.`)
    if (projectRunning.length) {
      lines.push('Currently running:')
      for (const t of projectRunning) {
        const started = absTime(t.started_at)
        lines.push(`  • ${taskKindLabel(t)} — ${taskLabel(t)} [${t.status}]${started ? ` (started ${started})` : ''}`)
      }
    }
    if (projectQueued.length) lines.push(`Queued tasks: ${projectQueued.length}`)
    if (activity?.stats) {
      lines.push(`Task history: ${activity.stats.completed} completed, ${activity.stats.failed} failed.`)
    }
    // Test runs: one COMPACT line per round — id, title, status, timings, and counts of what's inside.
    // The nested journeys/explorations/bugs (and their ids) are NOT dumped here; the agent drills into a
    // specific run with read_test_runs(test_run_id).
    //
    // Only the most recent MAX_CONTEXT_TEST_RUNS are listed (the API returns them newest-first). The
    // total and the number omitted are both stated, so the agent reads this as a window rather than the
    // whole history, and is pointed at the tool that can see the rest.
    if (testSessions.length) {
      const shown = testSessions.slice(0, MAX_CONTEXT_TEST_RUNS)
      const omitted = testSessionsTotal - shown.length
      lines.push(omitted > 0
        ? `Test runs — ${testSessionsTotal} total, ${shown.length} most recent listed here (id, title, status, counts). Use read_test_runs(test_run_id) to see the journeys/explorations/bugs inside one:`
        : `Test runs (${testSessionsTotal}) — id, title, status, counts. Use read_test_runs(test_run_id) to see the journeys/explorations/bugs inside one:`)
      for (const s of shown) {
        const times = [
          s.started_at ? `started ${absTime(s.started_at)}` : '',
          s.completed_at ? `completed ${absTime(s.completed_at)}` : '',
        ].filter(Boolean).join(', ')
        const counts = `${s.journeyRuns.length} journey run(s), ${s.explorations.length} exploration(s), ${s.bugs.length} bug(s)`
        lines.push(`  • ${s.id} — "${s.title}" — ${s.status} — ${counts}${times ? ` (${times})` : ''}`)
      }
      // Name the omission rather than letting the list read as complete, and steer the lookup toward a
      // FILTERED call — read_test_runs with no arguments returns every run, which is the same payload
      // this cap exists to keep out of the prompt.
      if (omitted > 0) {
        lines.push(`  • …${omitted} older run(s) not listed. To answer about those, call read_test_runs with a \`query\` (a PR number, branch, or run title) rather than listing every run.`)
      }
    }
    // Recent tasks with concrete timestamps (created/started/completed) as a fallback timeline — useful
    // for questions about individual runs the test-run grouping doesn't spell out.
    const recent = activity?.recent ?? []
    if (recent.length) {
      lines.push('Recent tasks (with timestamps):')
      for (const t of recent) {
        const stamp = t.completed_at
          ? `completed ${absTime(t.completed_at)}`
          : t.started_at
            ? `started ${absTime(t.started_at)}`
            : `created ${absTime(t.created_at)}`
        lines.push(`  • ${taskKindLabel(t)} — ${taskLabel(t)} (${t.status}${stamp ? `, ${stamp}` : ''})`)
      }
    }
    return lines.join('\n')
  }, [pathname, search, project, journeyTotal, journeyStatusCounts, bugTotal, bugOpen, projectRunning, projectQueued, activity, testSessions])

  // Open something the chat points at (currently a test run) in the main project view on the right.
  const openInProjectView = useCallback((path: string) => {
    navigate(path)
    onOpenProjectItem?.()
  }, [navigate, onOpenProjectItem])

  const appendAssistant = useCallback((text: string) => {
    const msg = createMessage('assistant', text)
    setMessages((cur) => {
      const next = [...cur, msg]
      messagesRef.current = next
      return next
    })
    persistMessages([msg])
    markAssistantSeen([msg], true)
  }, [persistMessages, markAssistantSeen])

  const runTurn = useCallback(async (userText: string) => {
    setError(null)
    setPending(true)
    setPendingChoices(null)
    setAgentStatus(null)
    const userMsg = createMessage('user', userText)
    // Stamp our own identity so the turn is attributed to us immediately (the server re-stamps the same
    // author authoritatively when it persists; this just makes the optimistic render consistent).
    if (currentUser) {
      userMsg.authorId = currentUser.id
      userMsg.authorName = currentUser.name
    }
    const base = [...messagesRef.current, userMsg]
    messagesRef.current = base
    setMessages(base)
    persistMessages([userMsg])
    // Trim before the POST rather than shipping the whole history for the server to throw away. The
    // history is re-sent in full on every turn and never shrinks: on a project whose chat collects QA's
    // automated notices that reached ~2,700 messages (~1.7MB per turn) to keep the ~60KB the server
    // actually uses — on its way to the 6MB function payload limit, past which chat stops working at all.
    // Same function the server re-applies, so this drops exactly what it would have dropped.
    const full = toWireMessages(base)
    const capped = capTranscript(full)
    const turnCtx: ChatTurnContext = {
      mode: 'project',
      projectId,
      turnCount: full.length,
      // Deliberately the PRE-trim size: this feeds stall diagnostics, where the question is how large the
      // conversation has grown, not how much of it survived the cap.
      transcriptChars: full.reduce((n, m) => n + m.content.length, 0),
    }
    try {
      await runChatTurn(
        { mode: 'project', projectId, timezone: currentChatTimezone(), context: buildContext(), messages: capped.messages, trimmed: capped.trimmed },
        {
          onStall: (stats) => reportChatStall(stats, turnCtx),
          onStatus: (s) => setAgentStatus(s),
          // Unused on this transport (a poll reports whole text, not deltas) but required by the shared
          // handler type; onText below is what actually renders the reply.
          onDelta: () => {},
          onText: (id, fullText) => {
            setAgentStatus(null)
            setMessages((cur) => {
              // REPLACE, not append: each poll carries the whole reply so far, which is what lets a
              // duplicated or retried poll be harmless. See replaceMessage in chat/chatModel.ts.
              const next = replaceMessage(cur, { id, role: 'assistant', content: fullText, createTime: new Date().toISOString() })
              messagesRef.current = next
              return next
            })
            // The stable id means only the first text of a reply raises the cue.
            markAssistantSeen([{ id, role: 'assistant' }], true)
          },
          // The server has ALREADY applied this (applyProjectSettingsForChat) and the agent's own reply
          // reports the real outcome, so there is nothing to do here but pull the new values in. This
          // used to be the browser's job — an event the server fired and hoped landed — which meant a
          // dropped connection silently discarded the user's change while the agent said it was done.
          onSettings: () => { void refetchProject() },
          onForm: (form) => setPendingForm(form),
          onRefresh: () => {
            // The agent did something that changes project state — kicked off real QA work (a new
            // exploration or journey re-run), changed a bug's status, or deleted a test run / journey /
            // exploration. Re-fetch the activity timeline so a newly queued task shows up, plus the bug
            // counts, journey counts, and test sessions so a status change or deletion is reflected
            // without waiting for the poll.
            void processingQuery.refetch()
            void activityQuery.refetch()
            void testSessionsQuery.refetch()
            void journeyQuery.refetch({ refetchCachedPages: false })
            void refetchProject()
          },
          onChoices: (choices) => setPendingChoices(choices),
          onGithub: (focus, repoUrl, resumePrompt) => setGithubPanel({ focus, repoUrl, resumePrompt }),
          onIssueTracker: (provider) => setIssueTracker({ provider }),
          // Also already applied server-side (setProjectStatusForChat) — same reasoning as onSettings.
          onProjectStatus: () => { void refetchProject() },
        },
      )
      // The turn is over and the SERVER has already written its reply into the transcript
      // (finalizeChatTurn), under the same message id this bubble carries — so this POST is now a
      // belt-and-braces no-op on a row that exists, kept only so an older deployment of this component
      // and the current server agree. It is what the next phase deletes.
      persistMessages(messagesRef.current)
    } catch (e) {
      // Report it (see chatTelemetry: nothing else records chat failures) and show it.
      reportChatFailure(e, turnCtx)
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
      // Keep whatever partial text did stream (it's part of the answer).
      persistMessages(messagesRef.current)
      // The failure NOTICE is only ours to write when the server has no record of the turn — i.e. the
      // start request itself never produced a row. Once a turn exists, the server ends it (or the poll's
      // expiry does) and writes the notice against the turn's own message id; writing a second one here
      // under a fresh id would leave the conversation apologising to itself twice.
      if (!turnExists(e)) appendAssistant(chatFailureNotice(e))
    } finally {
      setAgentStatus(null)
      setPending(false)
    }
  }, [activityQuery, appendAssistant, buildContext, currentUser, journeyQuery, markAssistantSeen, persistMessages, processingQuery, projectId, refetchProject, testSessionsQuery])

  // Let other parts of the app (e.g. the Help overlay's example questions) drop a prompt into this
  // chat and send it. Route through a ref so we can subscribe once without re-binding as runTurn's
  // identity changes, and ignore requests while a turn is in flight or an inline form is open.
  const runTurnRef = useRef(runTurn)
  runTurnRef.current = runTurn
  const busyRef = useRef(false)
  // The ownership card is not a form to fill in and answer: publishing that file takes a deploy, so it
  // sits in the transcript while the conversation carries on around it. Only a real form blocks input.
  const blockingForm = pendingForm && pendingForm.kind !== 'security-nonce' ? pendingForm : null
  busyRef.current = pending || !!blockingForm
  useEffect(() => onChatPromptRequest((text) => {
    if (busyRef.current) return
    void runTurnRef.current(text)
  }), [])

  // The softer "fill, don't send" channel — the read-only settings page's "Change in chat" deep-links
  // drop a prompt into the composer for the user to complete. Two sources: a live event (chat already
  // mounted, e.g. behind the settings modal) and a prompt stashed before navigating here from the
  // standalone settings tab (consumed once on mount). A monotonic seq lets the same text re-apply.
  const [fillSignal, setFillSignal] = useState<{ text: string; seq: number } | null>(null)
  const fillSeqRef = useRef(0)
  const fillInput = useCallback((text: string) => {
    fillSeqRef.current += 1
    setFillSignal({ text, seq: fillSeqRef.current })
  }, [])
  useEffect(() => {
    const pending = takePendingChatPrompt()
    if (pending) fillInput(pending)
    return onChatPromptFillRequest((text) => {
      takePendingChatPrompt() // clear any stash so it can't re-fill on a later mount
      fillInput(text)
    })
  }, [fillInput])

  // The project's current login handling mode (set on the login_mode global variable). 'waiting' means
  // an exploration hit a sign-in screen it couldn't get past and QA is waiting on the user to decide.
  const loginMode = (project?.global_variables ?? []).find((v) => v.name === 'login_mode')?.init as LoginMode | undefined

  // Re-show the login-pause choices if a *new* pause happens after an earlier one was resolved (i.e.
  // login_mode transitions back into 'waiting'). On that transition also nudge the transcript to its
  // live edge so the "⚠️ I paused the exploration…" notice + choices are surfaced even if the reader
  // had scrolled up (the notice arrives asynchronously via the poll, which the scroller won't follow).
  const [scrollToEndSignal, setScrollToEndSignal] = useState(0)
  const prevLoginModeRef = useRef<LoginMode | undefined>(loginMode)
  useEffect(() => {
    if (loginMode === 'waiting' && prevLoginModeRef.current !== 'waiting') {
      setLoginPauseResolved(false)
      setScrollToEndSignal((n) => n + 1)
    }
    prevLoginModeRef.current = loginMode
  }, [loginMode])

  // The user submitted the inline login form: turn the credentials into the project's login global
  // variables (merged with any non-login variables already set). When this answers a paused login wall
  // (login_mode 'waiting'), route through resolve-login-wall so it also resumes the paused exploration
  // in place (or queues a fresh run if it already exited). In every case, saving credentials makes them
  // the active login policy; retaining an earlier `none` mode would hide the credentials from exploration.
  const submitLogins = useCallback((result: { logins: LoginEntry[]; autoCreateAccount: boolean }) => {
    setPendingForm(null)
    const resolvingPause = loginMode === 'waiting'
    if (resolvingPause) setLoginPauseResolved(true)
    const merged = mergeProvidedLoginGlobalVariables(project?.global_variables ?? [], result.logins)
    let data: Record<string, unknown>
    if (resolvingPause) {
      // Backend confirms login_mode='provided' and drives the resume/queue.
      data = { action: 'resolve-login-wall', mode: 'provided', global_variables: merged }
    } else {
      data = { action: 'update-global-variables', global_variables: merged }
    }
    updateProject({ id: projectId, data })
      .unwrap()
      .then(() => appendAssistant(`✓ Saved ${result.logins.length} login${result.logins.length === 1 ? '' : 's'} to the project.${resolvingPause ? ' QA is resuming now.' : ''}`))
      .catch(() => appendAssistant('⚠️ I couldn’t save those credentials — please try again in a moment.'))
  }, [appendAssistant, loginMode, project, projectId, updateProject])

  // Resolve a login pause to a non-credential answer ('create' = QA registers its own test account,
  // 'none' = skip signed-in areas). Routes through resolve-login-wall, which sets login_mode and resumes
  // the paused exploration in place (or queues a fresh run if it already exited).
  const applyLoginMode = useCallback((mode: LoginMode, confirmation: string) => {
    updateProject({ id: projectId, data: { action: 'resolve-login-wall', mode } })
      .unwrap()
      .then(() => appendAssistant(confirmation))
      .catch(() => appendAssistant('⚠️ I couldn’t update that — please try again in a moment.'))
  }, [appendAssistant, projectId, updateProject])

  // A choice button was clicked in the chat (agent offered it via offer_choices). A `ui` action reveals
  // inline UI immediately (the add-logins form) with no model round-trip; a `message` action sends the
  // agent the choice's message as the next turn.
  const handleChatChoice = useCallback((choice: ChatChoice) => {
    setPendingChoices(null)
    if (choice.action.kind === 'ui') {
      if (choice.action.ui === 'logins') setPendingForm({ kind: 'logins', title: 'Add a test login' })
      return
    }
    void runTurn(choice.action.message)
  }, [runTurn])

  // A choice button was clicked on the built-in "exploration paused for login" prompt. "Provide login
  // credentials" opens the add-logins form (no model round-trip); the others resolve the wall directly
  // so QA resumes without the model.
  const handleLoginPauseChoice = useCallback((choice: ChatChoice) => {
    if (choice.id === 'provide-logins') { setPendingForm({ kind: 'logins', title: 'Add a test login' }); return }
    setLoginPauseResolved(true)
    if (choice.id === 'create-account') {
      applyLoginMode('create', '✓ QA will register its own test account and is resuming now.')
    } else {
      applyLoginMode('none', '✓ QA will skip signed-in areas and only test what’s reachable without logging in.')
    }
  }, [applyLoginMode])

  // Open a round's own page in the main project view. Client-side navigation keeps the chat sidebar
  // mounted (AppShell keeps chat mode on these project routes), so the run slides into the main pane
  // without dropping the conversation.
  const basePath = `/projects/${projectId}`
  const openTestRun = useCallback((session: TestSession) => {
    openInProjectView(`${basePath}/test-runs/${session.id}`)
  }, [basePath, openInProjectView])

  // Interleave the QA test-run entries inline with the conversation, ordered by time, so the
  // "what QA has been doing" updates read as part of the chat rather than a block pinned above it.
  const items = useMemo<ChatListItem[]>(() => {
    type Entry = { time: number; message?: ChatMessageData; run?: TestSession }
    const entries: Entry[] = []

    messages.forEach((m, idx) => {
      // The live recovery card owns access warnings; retain notices only in the model transcript.
      if (m.source?.startsWith('access-request:')) return
      const t = taskTimeMs(m.createTime)
      entries.push({ time: Number.isNaN(t) ? idx : t, message: m })
    })

    // Every round sorts by when it started, in-progress ones included. An in-progress round used to be
    // pinned to the live edge instead, which meant its card MOVED the moment the round completed — and a
    // reorder that leaves the row count unchanged makes the message-scroller re-anchor to the oldest turn
    // it has not already handled, i.e. the transcript jumped back to its very first message. Sessions load
    // slightly after task history, so this is intentionally the only activity view — rendering individual
    // task rows as a fallback would flash them in before these entries replaced them on cold load.
    for (const s of testSessions) {
      const ms = taskTimeMs(s.started_at)
      entries.push({ time: Number.isNaN(ms) ? 0 : ms, run: s })
    }

    // Stable sort keeps the messages' relative order on time ties.
    entries.sort((a, b) => a.time - b.time)

    // Consecutive rounds — nothing said between them — share one stable layout row while remaining
    // sibling, top-level Test Run cards. The row is keyed by its FIRST round: rounds are appended as
    // they start, so it can absorb a new card without reordering the transcript or causing a scroll jump.
    const list: ChatListItem[] = []
    let group: TestSession[] = []
    const flushGroup = () => {
      const first = group[0]
      if (!first) return
      const runs = group
      group = []
      list.push({ id: `runs-${first.id}`, node: (
        <TestRunEntry runs={runs} onOpen={openTestRun} pause={projectPause(project)} />
      ) })
    }
    for (const e of entries) {
      if (e.run) {
        group.push(e.run)
        continue
      }
      flushGroup()
      if (e.message) {
        const m = e.message
        list.push({ id: m.id, message: m, anchor: m.role === 'user' && !historyMessageIdsRef.current.has(m.id) })
      }
    }
    flushGroup()
    return list
  }, [messages, testSessions, openTestRun, project?.status])

  // Interactive dialogs (logins form, agent-offered choices, the login-pause prompt, and the GitHub /
  // issue-tracker panels) render as trailing entries INSIDE the scroll rather than as fixed panels
  // below it — so a tall dialog flows with the conversation and stays scrollable instead of clipping
  // or squeezing the transcript. Same show conditions as before; appended after the transcript.
  const dialogItems: ChatListItem[] = []
  dialogItems.push({ id: '__dialog-access-requests', node: <ChatAccessRequests key={projectId} projectId={projectId} /> })
  if (pendingForm?.kind === 'deployment-access') {
    dialogItems.push({ id: '__dialog-vercel-access', node: <VercelAccessSettings projectId={projectId} canEdit /> })
  }
  if (pendingForm?.kind === 'logins') {
    dialogItems.push({ id: '__dialog-logins', node: (
      <ChatLoginsForm
        title={pendingForm.title}
        initialLogins={(project?.global_variables ?? [])
          .filter((v) => v.name === 'login_email')
          .map((v) => ({ email: v.init ?? '', password: '' }))}
        onSubmit={submitLogins}
        onCancel={() => setPendingForm(null)}
      />
    ) })
  }
  // The ownership step for an existing project, revealed by the agent's setup_security_pass tool. The
  // nonce comes from the project the tool just wrote, never minted here: a second value would
  // invalidate a file the user may already have published.
  if (pendingForm?.kind === 'security-nonce' && project?.security_probing_enabled && project.security_nonce) {
    dialogItems.push({ id: '__dialog-security', node: (
      <SecurityOwnershipCard
        projectId={project.id}
        targetUrl={project.target_url ?? ''}
        value={{ nonce: project.security_nonce, verified: securityChecked?.verified === true, waived: project.security_ownership_exempt === true }}
        onChange={setSecurityChecked}
      />
    ) })
  }
  if (pendingChoices && !blockingForm) {
    dialogItems.push({ id: '__dialog-choices', node: (
      <ChatChoices choiceSet={pendingChoices} onSelect={handleChatChoice} disabled={pending} />
    ) })
  }
  if (loginMode === 'waiting' && !loginPauseResolved && !blockingForm && !pendingChoices) {
    dialogItems.push({ id: '__dialog-login', node: (
      <ChatChoices choiceSet={loginChoiceSet('How would you like QA to handle the login it hit?')} onSelect={handleLoginPauseChoice} />
    ) })
  }
  if (githubPanel && project) {
    const resumePrompt = githubPanel.resumePrompt
    dialogItems.push({ id: '__dialog-github', node: (
      <ProjectGithubChatCard
        project={project}
        focus={githubPanel.focus}
        initialRepoUrl={githubPanel.repoUrl}
        onClose={() => setGithubPanel(null)}
        onResult={appendAssistant}
        onConnected={resumePrompt ? () => { void runTurn(`GitHub is connected. Now complete my earlier request: ${resumePrompt}`) } : undefined}
      />
    ) })
  }
  if (issueTracker && project) {
    dialogItems.push({ id: '__dialog-issue', node: (
      <IssueTrackerChatCard project={project} provider={issueTracker.provider} onClose={() => setIssueTracker(null)} onResult={appendAssistant} />
    ) })
  }

  const lastMsg = messages[messages.length - 1]
  // Only show the typing indicator before the first streamed token — after that the assistant bubble
  // fills in place.
  const showTyping = pending && (!lastMsg || lastMsg.role === 'user')

  return (
    <div className="pchat" data-testid="project-chat">
      <div className="pchat__body">
        <ChatMessageList
          items={[...items, ...dialogItems]}
          pending={pending}
          typing={showTyping}
          pendingLabel={agentStatus ?? 'QA is thinking…'}
          showMessageNavigator
          historyLoading={chatQuery.isLoading}
          scrollToEndSignal={scrollToEndSignal}
          currentUserId={currentUser?.id ?? null}
          emptyState={<div className="chat-empty">Ask about your project&apos;s journeys, bugs, or activity — or request a settings change.</div>}
        />
      </div>

      {error && <p className="pchat__error" role="alert">{error}</p>}
      <ChatInput onSend={runTurn} disabled={pending || !!blockingForm} placeholder="Ask QA a question or change a setting…" fillSignal={fillSignal ?? undefined} />
    </div>
  )
}
